import React from 'react';
import { FileText, Upload, Calendar, CheckCircle, FileCheck, Send, ThumbsUp, Home as HomeIcon, HeadphonesIcon } from 'lucide-react';

const Process = () => {
  const steps = [
    {
      icon: FileText,
      number: '01',
      title: 'Begin with Trust',
      description: 'Sign our Credit Guide and Privacy Consent form for clarity and confidentiality',
    },
    {
      icon: Upload,
      number: '02',
      title: 'Share Your Story',
      description: 'Complete our Online Fact Find, giving insights into your financial aspirations',
    },
    {
      icon: Upload,
      number: '03',
      title: 'Easy Uploads',
      description: 'Seamlessly upload supporting documents via our secure platform',
    },
    {
      icon: Calendar,
      number: '04',
      title: 'Strategy Session',
      description: 'Schedule a broker appointment to discuss your goals',
    },
    {
      icon: CheckCircle,
      number: '05',
      title: 'Expert Recommendations',
      description: 'Your dedicated broker presents options for your consideration',
    },
    {
      icon: FileCheck,
      number: '06',
      title: 'Making It Official',
      description: 'Agree upon and sign the Credit Proposal',
    },
    {
      icon: Send,
      number: '07',
      title: 'Application in Action',
      description: 'We submit your application with precision at every step',
    },
    {
      icon: ThumbsUp,
      number: '08',
      title: 'Celebrating Approvals',
      description: 'Relish the moment as your application gets the green light',
    },
    {
      icon: HomeIcon,
      number: '09',
      title: 'Settlement & Success',
      description: 'Witness your application come to fruition as it settles',
    },
    {
      icon: HeadphonesIcon,
      number: '10',
      title: 'Ongoing Support',
      description: 'Our commitment continues beyond settlement throughout your financial journey',
    },
  ];

  return (
    <section id="process" className="py-20 bg-gradient-to-br from-gray-50 to-white" data-testid="process-section">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-flex items-center justify-center space-x-2 bg-[#99DF63]/10 px-6 py-2 rounded-full mb-6">
            <CheckCircle className="w-5 h-5 text-[#00594C]" />
            <span className="text-[#00594C] font-semibold">Our Process</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#00594C] mb-4" data-testid="process-title">
            Your Journey, Simplified
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A straightforward 10-step process that places you at the forefront
          </p>
        </div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#00594C] via-[#99DF63] to-[#00594C] transform -translate-x-1/2" />

          <div className="space-y-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isEven = index % 2 === 0;
              return (
                <div
                  key={index}
                  className={`relative flex items-center ${
                    isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } flex-col lg:gap-16`}
                  data-testid={`process-step-${index}`}
                >
                  {/* Content */}
                  <div className={`w-full lg:w-5/12 ${
                    isEven ? 'lg:text-right' : 'lg:text-left'
                  } text-center mb-8 lg:mb-0`}>
                    <div className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all card-hover ${
                      isEven ? 'lg:ml-auto' : 'lg:mr-auto'
                    }`}>
                      <div className={`flex items-center gap-4 ${
                        isEven ? 'lg:flex-row-reverse' : 'lg:flex-row'
                      } flex-row justify-center lg:justify-start`}>
                        <div className="w-16 h-16 bg-gradient-to-br from-[#00594C] to-[#003d35] rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-8 h-8 text-[#99DF63]" />
                        </div>
                        <div className={isEven ? 'lg:text-right' : 'lg:text-left'}>
                          <div className="text-sm font-bold text-[#99DF63] mb-1">Step {step.number}</div>
                          <h3 className="text-xl font-bold text-[#00594C]">{step.title}</h3>
                        </div>
                      </div>
                      <p className={`text-gray-600 mt-4 ${
                        isEven ? 'lg:text-right' : 'lg:text-left'
                      } text-center`}>
                        {step.description}
                      </p>
                    </div>
                  </div>

                  {/* Center Circle */}
                  <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-[#99DF63] rounded-full border-4 border-white shadow-lg z-10" />

                  {/* Spacer for opposite side */}
                  <div className="hidden lg:block w-5/12" />
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <a
            href="/#book-appointment"
            className="inline-flex items-center justify-center space-x-2 bg-[#99DF63] text-[#00594C] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#8BCF52] transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            data-testid="process-cta-button"
          >
            <span>Start Your Journey Today</span>
            <Calendar className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Process;